import React, { useEffect, useState } from 'react';
import {
  Card,
  Table,
  Input,
  Button,
  Modal,
  Form,
  InputNumber,
  message,
  Tag,
  Space,
  Typography,
  Statistic,
  Row,
  Col,
  Tabs,
  Select,
  DatePicker,
} from 'antd';
import {
  SearchOutlined,
  DollarOutlined,
  PlusOutlined,
  MinusOutlined,
  HistoryOutlined,
  EyeOutlined,
  UserOutlined,
  MailOutlined,
  IdcardOutlined,
  CreditCardOutlined,
  CalendarOutlined,
  BankOutlined,
  StopOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import api from '../../services/api';
import type { ColumnsType } from 'antd/es/table';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { RangePicker } = DatePicker;

interface Tenant {
  id: string;
  name: string;
  company_name: string;
  email: string;
  plan_code: string;
  current_balance: number;
  total_consumed: number;
  created_at: string;
  status: string;
}

interface Transaction {
  id: string;
  transaction_type: string;
  amount: number;
  balance_before: number;
  balance_after: number;
  description: string;
  created_at: string;
  created_by: string;
}

const TenantManagementPage: React.FC = () => {
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [adjustModalVisible, setAdjustModalVisible] = useState(false);
  const [transactionsModalVisible, setTransactionsModalVisible] = useState(false);
  const [detailsModalVisible, setDetailsModalVisible] = useState(false);
  const [statusModalVisible, setStatusModalVisible] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState<Tenant | null>(null);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [transactionsLoading, setTransactionsLoading] = useState(false);
  const [statusLoading, setStatusLoading] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchTenants();
  }, []);

  const fetchTenants = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/super-admin/tenants');
      setTenants(response.data.tenants || []);
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to load tenants');
    } finally {
      setLoading(false);
    }
  };

  const fetchTransactions = async (tenantId: string) => {
    try {
      setTransactionsLoading(true);
      const response = await api.get(`/api/super-admin/tenants/${tenantId}/transactions`);
      setTransactions(response.data.transactions || []);
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to load transactions');
    } finally {
      setTransactionsLoading(false);
    }
  };

  const handleAdjustCredits = (tenant: Tenant) => {
    setSelectedTenant(tenant);
    form.resetFields();
    setAdjustModalVisible(true);
  };

  const handleViewTransactions = (tenant: Tenant) => {
    setSelectedTenant(tenant);
    setTransactionsModalVisible(true);
    fetchTransactions(tenant.id);
  };

  const handleViewDetails = (tenant: Tenant) => {
    setSelectedTenant(tenant);
    setDetailsModalVisible(true);
  };

  const handleStatusChange = (tenant: Tenant) => {
    setSelectedTenant(tenant);
    setStatusModalVisible(true);
  };

  const handleConfirmStatusChange = async () => {
    if (!selectedTenant) return;
    
    try {
      setStatusLoading(true);
      const newStatus = selectedTenant.status === 'active' ? 'inactive' : 'active';
      
      await api.put(`/api/super-admin/tenants/${selectedTenant.id}/status`, {
        status: newStatus,
        reason: `Status changed via super admin portal`
      });
      
      message.success(`Tenant ${newStatus === 'active' ? 'activated' : 'deactivated'} successfully`);
      setStatusModalVisible(false);
      fetchTenants();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to update tenant status');
    } finally {
      setStatusLoading(false);
    }
  };

  const handleSubmitAdjustment = async () => {
    try {
      const values = await form.validateFields();
      
      await api.post(`/api/super-admin/tenants/${selectedTenant?.id}/credits/adjust`, {
        amount: values.amount,
        reason: values.reason,
      });
      
      message.success('Credit adjustment successful');
      setAdjustModalVisible(false);
      fetchTenants();
    } catch (error: any) {
      if (error.errorFields) {
        return;
      }
      message.error(error.response?.data?.message || 'Failed to adjust credits');
    }
  };

  const filteredTenants = tenants.filter(tenant => {
    if (!searchText) return true;
    const search = searchText.toLowerCase();
    return (
      (tenant.name || '').toLowerCase().includes(search) ||
      (tenant.company_name || '').toLowerCase().includes(search) ||
      (tenant.email || '').toLowerCase().includes(search) ||
      (tenant.id || '').toLowerCase().includes(search)
    );
  });

  const tenantColumns: ColumnsType<Tenant> = [
    {
      title: 'Tenant',
      key: 'tenant',
      render: (_, record) => (
        <div>
          <Text strong>{record.company_name || record.name}</Text>
          <br />
          <Text type="secondary" style={{ fontSize: '12px' }}>{record.name}</Text>
          <br />
          <Text type="secondary" style={{ fontSize: '11px' }}>{record.email}</Text>
        </div>
      ),
    },
    {
      title: 'Tenant ID',
      dataIndex: 'id',
      key: 'id',
      render: (id: string) => <Text code>{id.substring(0, 12)}...</Text>,
    },
    {
      title: 'Plan',
      dataIndex: 'plan_code',
      key: 'plan_code',
      render: (plan: string) => (
        <Tag color={plan === 'ENTERPRISE' ? 'gold' : plan === 'PROFESSIONAL' ? 'blue' : 'green'}>
          {plan || 'N/A'}
        </Tag>
      ),
    },
    {
      title: 'Balance',
      dataIndex: 'current_balance',
      key: 'current_balance',
      render: (balance: number) => (
        <Statistic
          value={balance}
          suffix="credits"
          valueStyle={{
            fontSize: '14px',
            color: balance < 100 ? '#ff4d4f' : balance < 500 ? '#faad14' : '#52c41a',
          }}
        />
      ),
      sorter: (a, b) => a.current_balance - b.current_balance,
    },
    {
      title: 'Consumed',
      dataIndex: 'total_consumed',
      key: 'total_consumed',
      render: (consumed: number) => consumed?.toLocaleString() || '0',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={status === 'active' ? 'green' : 'red'}>
          {status?.toUpperCase() || 'UNKNOWN'}
        </Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space size="small">
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              handleViewDetails(record);
            }}
            size="small"
          >
            View
          </Button>
          <Button
            type="link"
            icon={<DollarOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              handleAdjustCredits(record);
            }}
            size="small"
          >
            Adjust
          </Button>
          <Button
            type="link"
            icon={<HistoryOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              handleViewTransactions(record);
            }}
            size="small"
          >
            History
          </Button>
          <Button
            type="link"
            icon={record.status === 'active' ? <StopOutlined /> : <CheckCircleOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              handleStatusChange(record);
            }}
            size="small"
            danger={record.status === 'active'}
            style={{ color: record.status === 'inactive' ? '#52c41a' : undefined }}
          >
            {record.status === 'active' ? 'Deactivate' : 'Activate'}
          </Button>
        </Space>
      ),
    },
  ];

  const transactionColumns: ColumnsType<Transaction> = [
    {
      title: 'Date',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date: string) => new Date(date).toLocaleString(),
    },
    {
      title: 'Type',
      dataIndex: 'transaction_type',
      key: 'transaction_type',
      render: (type: string) => {
        const colors: Record<string, string> = {
          purchase: 'green',
          consumption: 'red',
          adjustment: 'blue',
          refund: 'orange',
        };
        return <Tag color={colors[type] || 'default'}>{type?.toUpperCase()}</Tag>;
      },
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount: number) => (
        <Text style={{ color: amount > 0 ? '#52c41a' : '#ff4d4f', fontWeight: 'bold' }}>
          {amount > 0 ? '+' : ''}{amount}
        </Text>
      ),
    },
    {
      title: 'Before',
      dataIndex: 'balance_before',
      key: 'balance_before',
      render: (balance: number) => balance.toLocaleString(),
    },
    {
      title: 'After',
      dataIndex: 'balance_after',
      key: 'balance_after',
      render: (balance: number) => balance.toLocaleString(),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
    },
    {
      title: 'By',
      dataIndex: 'created_by',
      key: 'created_by',
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '24px' }}>
        <Title level={2}>Tenant Management</Title>
      </div>

      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Total Tenants"
              value={tenants.length}
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Total Credits"
              value={tenants.reduce((sum, t) => sum + (t.current_balance || 0), 0)}
              suffix="credits"
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Total Consumed"
              value={tenants.reduce((sum, t) => sum + (t.total_consumed || 0), 0)}
              suffix="credits"
              valueStyle={{ color: '#cf1322' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Low Balance"
              value={tenants.filter(t => t.current_balance < 100).length}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
      </Row>

      <Card>
        <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
          <Input
            placeholder="Search by company name, contact name, email, or tenant ID..."
            prefix={<SearchOutlined />}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ width: '100%', maxWidth: '450px' }}
            allowClear
          />
          <Text type="secondary" style={{ fontSize: '12px' }}>
            <EyeOutlined /> Click on any row to view tenant details
          </Text>
        </div>

        <Table
          columns={tenantColumns}
          dataSource={filteredTenants}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 10, showSizeChanger: true, showTotal: (total) => `Total ${total} tenants` }}
          onRow={(record) => ({
            onClick: () => handleViewDetails(record),
            style: { cursor: 'pointer' },
          })}
        />
      </Card>

      {/* Adjust Credits Modal */}
      <Modal
        title={<><DollarOutlined /> Adjust Credits for {selectedTenant?.company_name || selectedTenant?.name}</>}
        open={adjustModalVisible}
        onOk={handleSubmitAdjustment}
        onCancel={() => setAdjustModalVisible(false)}
        width={500}
      >
        {selectedTenant && (
          <>
            <Card size="small" style={{ marginBottom: '16px', backgroundColor: '#f5f5f5' }}>
              <Row gutter={16}>
                <Col span={12}>
                  <Text type="secondary">Current Balance:</Text>
                  <br />
                  <Text strong style={{ fontSize: '18px' }}>
                    {selectedTenant.current_balance.toLocaleString()} credits
                  </Text>
                </Col>
                <Col span={12}>
                  <Text type="secondary">Plan:</Text>
                  <br />
                  <Tag color="blue">{selectedTenant.plan_code}</Tag>
                </Col>
              </Row>
            </Card>

            <Form form={form} layout="vertical">
              <Form.Item
                name="amount"
                label="Credit Adjustment"
                rules={[
                  { required: true, message: 'Please enter amount' },
                  {
                    validator: (_, value) => {
                      if (value === 0) {
                        return Promise.reject('Amount cannot be zero');
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
                tooltip="Positive to add credits, negative to deduct"
              >
                <InputNumber
                  style={{ width: '100%' }}
                  placeholder="e.g., 1000 or -500"
                  addonBefore={<span>Credits</span>}
                />
              </Form.Item>

              <Form.Item
                name="reason"
                label="Reason"
                rules={[{ required: true, message: 'Please enter reason for adjustment' }]}
              >
                <TextArea
                  rows={3}
                  placeholder="e.g., Refund for service outage, Promotional bonus, Manual correction"
                />
              </Form.Item>
            </Form>
          </>
        )}
      </Modal>

      {/* Transactions History Modal */}
      <Modal
        title={<><HistoryOutlined /> Transaction History - {selectedTenant?.company_name || selectedTenant?.name}</>}
        open={transactionsModalVisible}
        onCancel={() => setTransactionsModalVisible(false)}
        width={1000}
        footer={null}
      >
        <Table
          columns={transactionColumns}
          dataSource={transactions}
          rowKey="id"
          loading={transactionsLoading}
          pagination={{ pageSize: 20 }}
          scroll={{ x: 800 }}
        />
      </Modal>

      {/* Tenant Details Modal */}
      <Modal
        title={
          <Space>
            <EyeOutlined />
            <span>Tenant Details</span>
          </Space>
        }
        open={detailsModalVisible}
        onCancel={() => setDetailsModalVisible(false)}
        width={700}
        footer={[
          <Button key="history" icon={<HistoryOutlined />} onClick={() => {
            setDetailsModalVisible(false);
            if (selectedTenant) handleViewTransactions(selectedTenant);
          }}>
            View History
          </Button>,
          <Button key="adjust" type="primary" icon={<DollarOutlined />} onClick={() => {
            setDetailsModalVisible(false);
            if (selectedTenant) handleAdjustCredits(selectedTenant);
          }}>
            Adjust Credits
          </Button>,
          <Button key="close" onClick={() => setDetailsModalVisible(false)}>
            Close
          </Button>,
        ]}
      >
        {selectedTenant && (
          <div style={{ padding: '8px 0' }}>
            <Card size="small" style={{ marginBottom: '16px' }}>
              <Row gutter={[16, 24]}>
                <Col span={24}>
                  <Space direction="vertical" size={0} style={{ width: '100%' }}>
                    <Text type="secondary">
                      <BankOutlined /> Company Name
                    </Text>
                    <Title level={4} style={{ margin: '4px 0 0 0' }}>
                      {selectedTenant.company_name || 'N/A'}
                    </Title>
                  </Space>
                </Col>

                <Col span={12}>
                  <Space direction="vertical" size={0}>
                    <Text type="secondary">
                      <UserOutlined /> Contact Name
                    </Text>
                    <Text strong style={{ fontSize: '14px' }}>
                      {selectedTenant.name}
                    </Text>
                  </Space>
                </Col>

                <Col span={12}>
                  <Space direction="vertical" size={0}>
                    <Text type="secondary">
                      <MailOutlined /> Email
                    </Text>
                    <Text strong style={{ fontSize: '14px' }}>
                      {selectedTenant.email}
                    </Text>
                  </Space>
                </Col>

                <Col span={12}>
                  <Space direction="vertical" size={0}>
                    <Text type="secondary">
                      <IdcardOutlined /> Tenant ID
                    </Text>
                    <Text code style={{ fontSize: '12px' }}>
                      {selectedTenant.id}
                    </Text>
                  </Space>
                </Col>

                <Col span={12}>
                  <Space direction="vertical" size={0}>
                    <Text type="secondary">
                      <CreditCardOutlined /> Plan
                    </Text>
                    <Tag
                      color={selectedTenant.plan_code === 'ENTERPRISE' ? 'gold' : selectedTenant.plan_code === 'PROFESSIONAL' ? 'blue' : 'green'}
                      style={{ marginTop: '4px', fontSize: '13px' }}
                    >
                      {selectedTenant.plan_code || 'N/A'}
                    </Tag>
                  </Space>
                </Col>

                <Col span={12}>
                  <Space direction="vertical" size={0}>
                    <Text type="secondary">Status</Text>
                    <Tag
                      color={selectedTenant.status === 'active' ? 'green' : 'red'}
                      style={{ marginTop: '4px', fontSize: '13px' }}
                    >
                      {selectedTenant.status?.toUpperCase() || 'UNKNOWN'}
                    </Tag>
                  </Space>
                </Col>

                <Col span={12}>
                  <Space direction="vertical" size={0}>
                    <Text type="secondary">
                      <CalendarOutlined /> Created At
                    </Text>
                    <Text strong style={{ fontSize: '14px' }}>
                      {new Date(selectedTenant.created_at).toLocaleString()}
                    </Text>
                  </Space>
                </Col>
              </Row>
            </Card>

            <Card title="Credit Summary" size="small">
              <Row gutter={16}>
                <Col span={12}>
                  <Statistic
                    title="Current Balance"
                    value={selectedTenant.current_balance}
                    suffix="credits"
                    valueStyle={{
                      color:
                        selectedTenant.current_balance < 100
                          ? '#ff4d4f'
                          : selectedTenant.current_balance < 500
                          ? '#faad14'
                          : '#52c41a',
                    }}
                  />
                </Col>
                <Col span={12}>
                  <Statistic
                    title="Total Consumed"
                    value={selectedTenant.total_consumed}
                    suffix="credits"
                    valueStyle={{ color: '#1890ff' }}
                  />
                </Col>
              </Row>
            </Card>
          </div>
        )}
      </Modal>

      {/* Status Change Confirmation Modal */}
      <Modal
        title={
          <Space>
            <ExclamationCircleOutlined style={{ color: '#faad14' }} />
            Confirm Status Change
          </Space>
        }
        open={statusModalVisible}
        onOk={handleConfirmStatusChange}
        onCancel={() => setStatusModalVisible(false)}
        confirmLoading={statusLoading}
        okText="Confirm"
        cancelText="Cancel"
        width={500}
      >
        {selectedTenant && (
          <div>
            <Text>
              Are you sure you want to{' '}
              <Text strong style={{ color: selectedTenant.status === 'active' ? '#ff4d4f' : '#52c41a' }}>
                {selectedTenant.status === 'active' ? 'DEACTIVATE' : 'ACTIVATE'}
              </Text>{' '}
              this tenant?
            </Text>
            
            <Card size="small" style={{ marginTop: '16px', backgroundColor: '#f5f5f5' }}>
              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                <div>
                  <Text type="secondary">Tenant: </Text>
                  <Text strong>{selectedTenant.company_name || selectedTenant.name}</Text>
                </div>
                <div>
                  <Text type="secondary">Email: </Text>
                  <Text>{selectedTenant.email}</Text>
                </div>
                <div>
                  <Text type="secondary">Current Status: </Text>
                  <Tag color={selectedTenant.status === 'active' ? 'green' : 'red'}>
                    {selectedTenant.status?.toUpperCase()}
                  </Tag>
                </div>
                <div>
                  <Text type="secondary">New Status: </Text>
                  <Tag color={selectedTenant.status === 'active' ? 'red' : 'green'}>
                    {selectedTenant.status === 'active' ? 'INACTIVE' : 'ACTIVE'}
                  </Tag>
                </div>
              </Space>
            </Card>

            {selectedTenant.status === 'active' && (
              <div style={{ marginTop: '16px', padding: '12px', backgroundColor: '#fff7e6', borderRadius: '4px', border: '1px solid #ffd666' }}>
                <Text type="warning" strong>
                  Warning:
                </Text>
                <Text type="secondary" style={{ display: 'block', marginTop: '8px' }}>
                  Deactivating this tenant may prevent their users from accessing the system and using API services.
                </Text>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default TenantManagementPage;
